import _ from "lodash";
import React, { useState, useRef, useEffect, useCallback } from "react";
import useIsMobile from "./useIsMobile";

import {
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  Menu,
  Pagination,
} from "@mui/material";
import SearchResultItem from "./SearchResultItem";
import FilterComponent from "./FilterComponent";
import qs from "query-string";
import { SearchAPIResult } from "./types/SearchAPIResult";
import { Filters } from "./types/Filters";
import { LoadDataParams } from "./types/LoadDataParams";
import { Pivot } from "./types/Pivot";
import { BrandModelResultModel } from "./types/BrandModelResultModel";
import { PartResultsModel } from "./types/PartResultsModel";
export const Search = ({
  data: {
    firmanavn,
    Orderby,
    SearchResults: {
      Results,
      TotalRows,
      PartGroup,
      Parts,
      Brand,
      Models,
      Price,
      FromYear,
      ToYear,
      haspictures,
      Mileage,
    },
    CurrentPageIndex,
    SearchType,
    SearchText,
    Firmanavn,
    NumResultPages,
    HasPictures,
  },
  PartFacets,
  BrandFacets,
  SiteUrl,
  searchUrls,
}) => {
  const currentYear = new Date().getFullYear();
  const yearArray = _.range(currentYear, currentYear - 25, -1).map((i) =>
    i.toString()
  );
  const yearValues = ["", ...yearArray];
  const isMobile = useIsMobile();
  // State initialization
  const [filters, setFilters] = useState({
    firmanavn,
    orderby: Orderby,
    part: Parts && Parts[0],
    brand: Brand,
    models: Brand && Models && Models[0],
    price: Price,
    fromyear: FromYear,
    toyear: ToYear,
    haspictures: HasPictures,
    mileage: Mileage,
    start: CurrentPageIndex,
    type: SearchType,
    partgroup: PartGroup,
    q: SearchText,
  });

  useEffect(() => {
    setFilters({
      firmanavn,
      orderby: Orderby,
      partgroup: PartGroup,
      part: PartGroup && Parts && Parts[0],
      brand: Brand,
      models: Brand && Models && Models[0],
      price: Price,
      fromyear: FromYear,
      toyear: ToYear,
      haspictures: haspictures,
      mileage: Mileage,
      start: CurrentPageIndex,
      type: SearchType,
      q: SearchText,
    });
  }, [
    firmanavn,
    Orderby,
    PartGroup,
    Parts,
    Brand,
    Models,
    Price,
    FromYear,
    ToYear,
    haspictures,
    Mileage,
    CurrentPageIndex,
    SearchType,
    SearchText,
  ]);

  const [fromYearOpen, setFromYearOpen] = useState(false);
  const [toYearOpen, setToYearOpen] = useState(false);
  const [sortDropdownAnchorEl, setSortDropdownAnchorEl] = useState(null);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [totalResults, setTotalResults] = useState(TotalRows);
  const [partFacets, setPartFacets] = useState(
    transformPartFacetsData(PartFacets)
  );
  const [brandFacets, setBrandFacets] = useState(
    transformBrandModelData(BrandFacets)
  );
  const [searchResults, setSearchResults] = useState(Results);
  const [isLoading, setIsLoading] = useState(false);
  const [numResults, setNumResults] = useState(NumResultPages);

  const [eventRegistered, setEventRegistered] = useState(false);
  const [mileageInput, setMileageInput] = useState(filters.mileage ?? "");
  const [loadDataParams, setLoadDataParams] = useState<LoadDataParams | null>(
    null
  );
  const [searchTextInput, setSearchTextInput] = useState(filters.q || "");

  function transformPartFacetsData(data: Pivot[]): PartResultsModel[] {
    return data
      .slice()
      .sort((a, b) => a.Value.localeCompare(b.Value, "nb-NO"))
      .map((maingroup) => ({
      text: maingroup.Value,
      count: maingroup.Count,
      fieldname: "maingroup",
      currentIsFilterable: false,
      children: (maingroup.ChildPivots || [])
        .slice()
        .sort((a, b) => a.Value.localeCompare(b.Value, "nb-NO"))
        .map((partgroup) => ({
        text: partgroup.Value,
        count: partgroup.Count,
        fieldname: "partgroup",
        currentIsFilterable: false,
        children: (partgroup.ChildPivots || [])
          .slice()
          .sort((a, b) => a.Value.localeCompare(b.Value, "nb-NO"))
          .map((part) => ({
          text: part.Value,
          fieldname: "part",
          count: part.Count,
          currentIsFilterable: true,
          activated:
            part.Value.trim().toLowerCase() ===
            (filters.part || "").trim().toLowerCase(),
          children: [],
          })),
      })),
    }));
  }

  function transformBrandModelData(data: Pivot[]): BrandModelResultModel[] {
    return data
      .slice() // copy array
      .sort((a, b) => a.Value.localeCompare(b.Value))
      .map((brand) => ({
        text: brand.Value,
        count: brand.Count,
        fieldname: "brand",
        currentIsFilterable: true,
        activated:
          brand.Value.trim().toLowerCase() ===
          (filters.brand || "").trim().toLowerCase(),
        children: (brand.ChildPivots || [])
          .slice() // copy array
          .sort((a, b) => a.Value.localeCompare(b.Value))
          .map((model) => ({
            text: model.Value,
            count: model.Count,
            fieldname: "models",
            currentIsFilterable: true,
            activated:
              model.Value.trim().toLowerCase() ===
              (filters.models || "").trim().toLowerCase(),
            children: [],
          })),
      }));
  }

  // Load data function
  const loadData = useCallback(
    async (fieldname, isupwards, scrolltop) => {
      setIsLoading(true);
      const url = window.location.href.split("?")[0];
      const data = new URLSearchParams(
        _.pickBy(filters, _.identity)
      ).toString();

      if (scrolltop) {
        const resultsHeader = document.getElementsByClassName(
          "partDetailsHitsAndSortingContainer"
        )[0] as HTMLElement | undefined;

        if (resultsHeader) {
          resultsHeader.scrollIntoView({
            block: isMobile ? "start" : "center",
            behavior: "smooth",
          });
        }
      }

      try {
        const response = await fetch("/api/Search/Index/?" + data, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });
        if (!response.ok) throw new Error("Network response was not ok");
        const result: SearchAPIResult = await response.json();

        setSearchResults(result.SearchResults.Results);
        setTotalResults(result.SearchResults.TotalRows);
        setNumResults(result.NumResultPages);

        if (fieldname === "brand" || fieldname === "models" || isupwards) {
          setPartFacets(
            transformPartFacetsData(result.SearchResults.PartFacets)
          );
        }
        if (fieldname === "part" || fieldname === "partgroup" || isupwards) {
          setBrandFacets(
            transformBrandModelData(result.SearchResults.ModelFacets)
          );
        }

        setIsLoading(false);

        const stateForHistory = getMinimalFilterState(filters);
        history.pushState(
          stateForHistory,
          "orderby:" + filters.orderby,
          url + "?" + data
        );
      } catch (error) {
        console.log("error", error);
      }
    },
    [filters]
  );

  const filterPart = (part, nodePath, isActive: boolean, fieldname: string) => {
    if (isActive == false) {
      handleFilterChange(fieldname, null);
    } else {
      handleFilterChange(fieldname, part.text);
    }
  };

  const filterBrandModel = (
    brand,
    nodePath,
    isActive: boolean,
    fieldname: string
  ) => {
    if (isActive == false) {
      handleFilterChange(fieldname, null);
    } else handleFilterChange(fieldname, brand.text);
  };
  // Event handlers
  const handleFilterChange = (
    fieldname: string,
    value: string | number | boolean | null
  ) => {
    let newValue = value;
    if (fieldname === "haspictures") {
      newValue = value === true ? "true" : value === false ? "" : value;
    }
    if (fieldname === "brand" && typeof value !== "string") {
      newValue = value ? String(value) : "";
    }
    setFilters((prev) => ({
      ...prev,
      [fieldname]: newValue,
      start: fieldname !== "start" ? 1 : value,
    }));

    setLoadDataParams({
      fieldname: fieldname,
      isupwards: true,
      scrolltop: fieldname === "start",
    });
  };

  useEffect(() => {
    if (loadDataParams) {
      loadData(
        loadDataParams.fieldname,
        loadDataParams.isupwards,
        loadDataParams.scrolltop
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const setOrderBy = (sortOrder: string) => {
    handleFilterChange("orderby", sortOrder);
    setLoadDataParams({
      fieldname: "orderby",
      isupwards: true,
      scrolltop: false,
    });
  };

  const debouncedMileageUpdate = useRef(
    _.debounce((value: string | number) => {
      handleFilterChange("mileage", value);
    }, 1000)
  ).current;

  // Effects
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    if (!eventRegistered) {
      window.addEventListener("popstate", (evt) => {
        if (evt.state) {
          // Only restore minimal filters, then trigger data reload
          setFilters((prev) => ({
            ...prev,
            ...getMinimalFilterState(evt.state),
          }));
          setLoadDataParams({
            fieldname: "popstate",
            isupwards: true,
            scrolltop: false,
          });
        }
      });
      setEventRegistered(true);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [eventRegistered, loadData]);

  // On mount, parse URL params and set filters if present
  useEffect(() => {
    const params = qs.parse(window.location.search);
    if (Object.keys(params).length > 0) {
      setFilters((prev) => ({
        ...prev,
        ...params,
        haspictures:
          params.haspictures === true || params.haspictures === "true"
            ? "true"
            : "",
        brand: params.brand ? decodeURIComponent(String(params.brand)) : "",
        start: params.start ? Number(params.start) : 1,
        mileage: params.mileage || "",
        fromyear: params.fromyear || "",
        toyear: params.toyear || "",
        q: params.q || "",
        part: params.part ? decodeURIComponent(params.part) : "",
        models: params.models ? decodeURIComponent(params.models) : "",
      }));
    }
  }, []);

  const shouldRenderFilters = screenWidth < 870 ? isFiltersVisible : true;

  return (
    <div className="choosePartContentWrapper">
      <div className="filterContainer">
        {/* Text search input with debounce */}
        <div style={{ marginBottom: 16 }}></div>
        <h2
          className="partDetailsFilterText"
          onClick={() => setIsFiltersVisible(!isFiltersVisible)}
        >
          <div>
            <img
              className="partDetailsFilterIcon"
              src="/images/filter.svg"
              alt="Filter"
            />
            {isFiltersVisible ? "Lukk filter" : "Begrens antall treff"}
          </div>
          <img
            className="partDetailsFilterIcon"
            src={
              isFiltersVisible ? "/images/CaretUp.svg" : "/images/CaretDown.svg"
            }
            alt="Toggle filters"
          />
        </h2>
        {shouldRenderFilters && (
          <div className="searchFilters">
            <strong className="partcounter">Antall treff {totalResults}</strong>
            <label>Søketekst</label>
            <input
              type="text"
              className="partDetailsFilterInput"
              placeholder="Søk tekst..."
              value={searchTextInput}
              onChange={(e) => setSearchTextInput(e.target.value)}
              style={{ marginRight: 8 }}
            />
            <button
              aria-label="Søk"
              className="filterSearchButton"
              onClick={() => handleFilterChange("q", searchTextInput)}
            >
              <img alt="Søk" src="/images/search.svg" width="20" height="20" />
            </button>
            <label htmlFor="milage">Merke/Modell</label>
            <FilterComponent
              filterLevel={2}
              data={brandFacets}
              showSearch={false}
              displayNames={["Merke", "Modell"]}
              onFiltered={filterBrandModel}
              filters={filters}
            />
            <label>Delenavn</label>
            <FilterComponent
              filterLevel={1}
              showSearch={true}
              data={partFacets}
              onFiltered={filterPart}
              filters={filters}
              showcheckboxes={false}
            />
            <label htmlFor="milage">Max km stand</label>
            <div className="filterInput">
              <input
                className="partDetailsFilterInput"
                onChange={(e) => {
                  setMileageInput(e.target.value); // update immediately for UI
                  debouncedMileageUpdate(e.target.value); // update filter after debounce
                }}
                type="text"
                name="mileage"
                id="milage"
                value={mileageInput}
              />
              <span className="filterUnit">km</span>
            </div>
            <label htmlFor="fromyear">Årsmodell</label>
            <div
              className="filterInput"
              onClick={() => setFromYearOpen(!fromYearOpen)}
            >
              <FormControl variant="outlined" fullWidth>
                <Select
                  className="partDetailsFilterInput"
                  id="fromyear"
                  value={filters.fromyear}
                  onChange={(e) =>
                    handleFilterChange("fromyear", e.target.value)
                  }
                  sx={{ fontSize: "16px" }}
                  IconComponent={() => null}
                  displayEmpty
                  renderValue={(selected) => selected || "Fra år"}
                  open={fromYearOpen}
                >
                  <MenuItem value="" disabled>
                    Fra år
                  </MenuItem>
                  {yearValues.map((item) => (
                    <MenuItem sx={{ fontSize: "16px" }} key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <img
                className="filterDropdownIcon"
                src="/images/CaretDown.svg"
                alt="Dropdown"
              />
            </div>
            <div
              className="filterInput"
              onClick={() => setToYearOpen(!toYearOpen)}
            >
              <FormControl variant="outlined" fullWidth>
                <Select
                  className="partDetailsFilterInput"
                  id="toyear"
                  value={filters.toyear}
                  onChange={(e) => handleFilterChange("toyear", e.target.value)}
                  sx={{ fontSize: "16px" }}
                  IconComponent={() => null}
                  displayEmpty
                  renderValue={(selected) => selected || "Til år"}
                  open={toYearOpen}
                >
                  <MenuItem value="" disabled>
                    Til år
                  </MenuItem>
                  {yearValues.map((item) => (
                    <MenuItem sx={{ fontSize: "16px" }} key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <img
                className="filterDropdownIcon"
                src="/images/CaretDown.svg"
                alt="Dropdown"
              />
            </div>
            <div className="partDetailsPictureFilterContainer">
              <input
                onChange={(e) =>
                  handleFilterChange(
                    "haspictures",
                    e.target.checked ? "true" : ""
                  )
                }
                type="checkbox"
                name="haspictures"
                id="haspictures"
                checked={filters.haspictures === "true"}
              />
              <label htmlFor="haspictures">Har bilder</label>
            </div>
          </div>
        )}
      </div>

      <div className="partDetailsSearchResultsWrapper">
        <div className="partDetailsHitsAndSortingContainer">
          <h3 className="partDetailsNumberHits">{totalResults} treff </h3>

          <div className="partDetailsSortWrapper">
            <p>Sortering</p>
            <img
              src="/images/sort.svg"
              onClick={(event) => setSortDropdownAnchorEl(event.currentTarget)}
              style={{ cursor: "pointer" }}
              alt="Sort"
            />
            <Menu
              anchorEl={sortDropdownAnchorEl}
              open={Boolean(sortDropdownAnchorEl)}
              onClose={() => setSortDropdownAnchorEl(null)}
            >
              <ListSubheader sx={{ fontSize: "16px" }}>Årsmodell</ListSubheader>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("CarYearModel");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Årsmodell nyest - eldst
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("CarYearModelAsc");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Årsmodell eldst - nyest
              </MenuItem>
              <ListSubheader sx={{ fontSize: "16px" }}>
                Kilometerstand
              </ListSubheader>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("Mileage");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Kilometerstand - lavest først
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("MileageDesc");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Kilometerstand - høyest først
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("OriginalNumber");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Originalnummer
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("ArticleNumber");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Artikkelnummer
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("EngineType");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Motor
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("Color");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Farge
              </MenuItem>
              <ListSubheader sx={{ fontSize: "16px" }}>Bedrift</ListSubheader>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("DealerName");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Bedrift - alfabetisk stigende
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("DealerNameDesc");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Bedrift - alfabetisk synkende
              </MenuItem>
              <ListSubheader sx={{ fontSize: "16px" }}>Pris</ListSubheader>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("Price");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Pris - høy - lav
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "16px" }}
                onClick={() => {
                  setOrderBy("PriceAsc");
                  setSortDropdownAnchorEl(null);
                }}
              >
                Pris - lav - høy
              </MenuItem>
            </Menu>
          </div>
        </div>

        <div
          className={
            isLoading === true
              ? "partDetailsSearchResults partDetailsSearchResults--loading"
              : "partDetailsSearchResults"
          }
        >
          {searchResults.map((object: SearchResultItem) => (
            <SearchResultItem
              firmanavn={firmanavn}
              key={object.StorageId}
              id={object.StorageId}
              firmnavn={Firmanavn}
              data={object}
              siteUrl={SiteUrl}
              searchUrls={searchUrls}
            />
          ))}
        </div>

        <Pagination
          count={numResults}
          shape="rounded"
          variant="outlined"
          page={filters.start}
          onChange={(event, value) => handleFilterChange("start", value)}
          color="primary"
          showFirstButton
          showLastButton
          size="large"
        />
      </div>
    </div>
  );
};

// Helper: get minimal filter state for history
function getMinimalFilterState(filters: Filters) {
  return {
    q: filters.q || "",
    brand: filters.brand || "",
    models: filters.models || "",
    mileage: filters.mileage || "",
    part: filters.part || "",
    fromyear: filters.fromyear || "",
    toyear: filters.toyear || "",
    haspictures: filters.haspictures || "",
    orderby: filters.orderby || "",
    start: filters.start || 1,
  };
}
